import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import ApiService from '../services/ApiService';

const XeroConnectionContext = createContext();

export const useXeroConnection = () => {
  const context = useContext(XeroConnectionContext);
  if (!context) {
    throw new Error('useXeroConnection must be used within XeroConnectionProvider');
  }
  return context;
};

export const XeroConnectionProvider = ({ children }) => {
  const [connectionStatus, setConnectionStatus] = useState({
    isConnected: false,
    tenantName: null,
    lastChecked: null,
    isChecking: false,
    error: null
  });

  const checkIntervalRef = useRef(null);
  const isCheckingRef = useRef(false);

  // Check connection status
  const checkConnection = useCallback(async (silent = false) => {
    // Prevent multiple simultaneous checks
    if (isCheckingRef.current) {
      console.log('⏭️ Skipping connection check - already in progress');
      return;
    }

    try {
      isCheckingRef.current = true;
      
      if (!silent) {
        setConnectionStatus(prev => ({ ...prev, isChecking: true, error: null }));
      }

      console.log('🔍 Checking Xero connection status...');
      const status = await ApiService.getConnectionStatus();
      
      const newStatus = {
        isConnected: status.isConnected || false,
        tenantName: status.tenantName || null,
        lastChecked: new Date().toISOString(),
        isChecking: false,
        error: null
      };

      console.log('✅ Connection status updated:', newStatus);
      setConnectionStatus(newStatus);

      return newStatus;
    } catch (error) {
      console.error('❌ Error checking Xero connection:', error);
      
      const errorStatus = {
        isConnected: false,
        tenantName: null,
        lastChecked: new Date().toISOString(),
        isChecking: false,
        error: error.message || 'Failed to check connection'
      };

      setConnectionStatus(errorStatus);
      return errorStatus;
    } finally {
      isCheckingRef.current = false;
    }
  }, []);

  // Start periodic checking (every 30 seconds)
  const startMonitoring = useCallback(() => {
    console.log('🚀 Starting Xero connection monitoring (30s interval)');
    
    // Clear any existing interval
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }

    // Initial check
    checkConnection(false);

    // Set up periodic checks every 30 seconds
    checkIntervalRef.current = setInterval(() => {
      console.log('⏰ Periodic Xero connection check (30s interval)');
      checkConnection(true); // Silent checks after initial
    }, 30000); // 30 seconds

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [checkConnection]);

  // Stop monitoring
  const stopMonitoring = useCallback(() => {
    console.log('🛑 Stopping Xero connection monitoring');
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
  }, []);

  // Force a manual refresh
  const refreshConnection = useCallback(async () => {
    console.log('🔄 Manual connection refresh requested');
    return await checkConnection(false);
  }, [checkConnection]);

  // Update connection status (for when connection is established elsewhere)
  const updateConnectionStatus = useCallback((status) => {
    console.log('📝 Manually updating connection status:', status);
    setConnectionStatus(prev => ({
      ...prev,
      ...status,
      lastChecked: new Date().toISOString()
    }));
  }, []);

  // Start monitoring when component mounts
  useEffect(() => {
    const cleanup = startMonitoring();
    return cleanup;
  }, [startMonitoring]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopMonitoring();
    };
  }, [stopMonitoring]);

  const value = {
    ...connectionStatus,
    checkConnection,
    refreshConnection,
    updateConnectionStatus,
    startMonitoring,
    stopMonitoring
  };

  return (
    <XeroConnectionContext.Provider value={value}>
      {children}
    </XeroConnectionContext.Provider>
  );
};

export default XeroConnectionContext;






